import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./footer.css";

import logo from "../../../assets/aigiologo.svg";
import { subscribe } from "../../../api/Subsciption/subscriptionApi";
import facebookIcon from "../../../assets/Facebook.svg";
import linkedinIcon from "../../../assets/Linkedin.svg";
import instagramIcon from "../../../assets/instagram.svg";
import youtubeIcon from "../../../assets/Youtube.svg";
import twitterIcon from "../../../assets/twitter-x-line.svg";

type FooterProps = {
  className?: string;
};

const Footer: React.FC<FooterProps> = ({ className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [messageType, setMessageType] = React.useState<"success" | "error">(
    "success",
  );

  const handleSectionNav = (
    event: React.MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    event.preventDefault();
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const handleRouteNav = (
    event: React.MouseEvent<HTMLAnchorElement>,
    path: string,
  ) => {
    event.preventDefault();
    navigate(path);
    // Scroll reset is handled globally by <ScrollToTop /> (instant, no smooth
    // animation).
  };

  const handleSubscribe = async () => {
    if (!email.trim()) {
      setMessageType("error");
      setMessage("Please enter your email.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const res = await subscribe({
        email,
        type: "newsletter",
      });

      setMessageType("success");
      setMessage(res.message);
      setEmail("");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Subscription failed.";

      setMessageType("error");
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className={`footer${className ? ` ${className}` : ""}`}>
      <div className="footer-container">
        <div className="footer-top">
          <div className="footer-left">
            <img src={logo} alt="AIGIO" className="footer-logo" />
            <p className="footer-tag">
            AIGIO is your AI Brand Manager—helping businesses research, build, launch, and grow stronger brands through one intelligent workflow.
            </p>

            <div className="footer-socials">
              <a
                className="footer-social"
                href="https://www.linkedin.com/company/aigio-ai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <img src={linkedinIcon} alt="LinkedIn" />
              </a>

              <a
                className="footer-social"
                href="https://www.facebook.com/profile.php?id=61591610803643"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src={facebookIcon} alt="Facebook" />
              </a>

              <a
                className="footer-social"
                href="https://www.instagram.com/aigio.ai/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src={instagramIcon} alt="Instagram" />
              </a>

              <a
                className="footer-social"
                href="https://www.youtube.com/@aigio-ai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <img src={youtubeIcon} alt="YouTube" />
              </a>

              <a
                className="footer-social"
                href="https://x.com/aigio_ai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <img src={twitterIcon} alt="Twitter" />
              </a>
            </div>
          </div>

          {/* DESKTOP LINKS */}
          <div className="footer-links desktop-footer-links">
            <div className="footer-col">
              <h4>PRODUCT</h4>
              <ul>
                <li>
                  <a
                    href="/#how-it-works"
                    onClick={(e) => handleSectionNav(e, "how-it-works")}
                  >
                    Workflow
                  </a>
                </li>
                <li>
                  <a
                    href="/#audit"
                    onClick={(e) => handleSectionNav(e, "audit")}
                  >
                    AI Audit
                  </a>
                </li>
                <li>
                  <a
                    href="/#pricing"
                    onClick={(e) => handleSectionNav(e, "pricing")}
                  >
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>COMPANY</h4>
              <ul>
                <li>
                  <a href="/about" onClick={(e) => handleRouteNav(e, "/about")}>
                    About us
                  </a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>

                <li>
                  <a
                    href="/contact"
                    onClick={(e) => handleRouteNav(e, "/contact")}
                  >
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>LEGAL</h4>
              <ul>
                <li>
                  <a
                    href="/privacy"
                    onClick={(e) => handleRouteNav(e, "/privacy")}
                  >
                    Privacy policy
                  </a>
                </li>
                <li>
                  <a href="/terms" onClick={(e) => handleRouteNav(e, "/terms")}>
                    Terms &amp; conditions
                  </a>
                </li>
                <li>
                  <a
                    href="/cookies"
                    onClick={(e) => handleRouteNav(e, "/cookies")}
                  >
                    Cookies policy
                  </a>
                </li>
                <li>
                  <a
                    href="/refund"
                    onClick={(e) => handleRouteNav(e, "/refund")}
                  >
                    Refund policy
                  </a>
                </li>
                <li>
                  <a
                    href="/shipping"
                    onClick={(e) => handleRouteNav(e, "/shipping")}
                  >
                    Shipping policy
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-newsletter-column">
              <p className="footer-newsletter-kicker">Join our mailing list</p>
              <div className="footer-newsletter-form">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="footer-newsletter-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="footer-newsletter-btn"
                  onClick={handleSubscribe}
                  disabled={loading}
                >
                  {loading ? "Joining..." : "Join"}
                </button>
              </div>
              {message && (
                <p
                  className={`footer-newsletter-message footer-newsletter-message--${messageType}`}
                >
                  {message}
                </p>
              )}
            </div>
          </div>

          {/* MOBILE LINKS */}
          <div className="footer-links mobile-footer-links">
            <div className="footer-col">
              <h4>COMPANY</h4>
              <ul>
                <li>
                  <a
                    href="/#how-it-works"
                    onClick={(e) => handleSectionNav(e, "how-it-works")}
                  >
                    How it works
                  </a>
                </li>
                <li>
                  <a
                    href="/#pricing"
                    onClick={(e) => handleSectionNav(e, "pricing")}
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="/#audit"
                    onClick={(e) => handleSectionNav(e, "audit")}
                  >
                    Free Audit
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>RESOURCES</h4>
              <ul>
                <li>
                  <a href="/about" onClick={(e) => handleRouteNav(e, "/about")}>
                    About
                  </a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
                <li>
                  <a href="/careers">Careers</a>
                </li>
                <li>
                  <a
                    href="/contact"
                    onClick={(e) => handleRouteNav(e, "/contact")}
                  >
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>LEGAL</h4>
              <ul>
                <li>
                  <a
                    href="/privacy"
                    onClick={(e) => handleRouteNav(e, "/privacy")}
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms" onClick={(e) => handleRouteNav(e, "/terms")}>
                    Terms &amp; Conditions
                  </a>
                </li>
                <li>
                  <a
                    href="/cookies"
                    onClick={(e) => handleRouteNav(e, "/cookies")}
                  >
                    Cookies Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/refunds"
                    onClick={(e) => handleRouteNav(e, "/refunds")}
                  >
                    Refund Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/shipping"
                    onClick={(e) => handleRouteNav(e, "/shipping")}
                  >
                    Shipping Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="footer-watermark-wrapper">
          <div className="footer-watermark">aigio</div>
        </div>

        {/* Bottom Section */}
        <div className="footer-bottom">
          <div className="copyright">© 2026 AIGIO. All rights reserved.</div>
          <div className="powered">
            *Powered By{" "}
            <a
              href="https://www.orglife.co.in"
              target="_blank"
              rel="noopener noreferrer"
            >
              OrgLife Industries Private Limited
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
